import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiTarget, FiArrowRight, FiArrowLeft, FiCalendar, FiTrendingUp, FiUsers } = FiIcons;

const Step2 = ({ formData, updateFormData, onNext, onPrev }) => {
  const goalOptions = [
    { value: 'launch-week-bestseller', label: 'Launch Week Bestseller', icon: FiTrendingUp },
    { value: 'build-audience', label: 'Build Email Audience', icon: FiUsers },
    { value: 'impact-ministry', label: 'Ministry Impact', icon: FiTarget },
    { value: 'speaking-gigs', label: 'Speaking Opportunities', icon: FiCalendar },
    { value: 'multiple', label: 'Multiple Goals', icon: FiTarget }
  ];

  const metricOptions = [
    '100-500 book sales',
    '500-1,000 book sales', 
    '1,000-5,000 book sales',
    '100 new email subscribers',
    '500 new email subscribers',
    '1,000+ new email subscribers',
    '10 speaking engagements',
    'Partnerships with 5+ ministries',
    'Other custom metric'
  ];

  const timelineOptions = [
    'Launch Week',
    'First Month',
    'First Quarter',
    'First 6 Months',
    'Full Year'
  ];

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  const canProceed = formData.launchGoal && formData.launchMetrics && formData.timeline;

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-xl p-8"
      >
        <div className="text-center mb-8">
          <div className="bg-gradient-to-r from-purple-600 to-pink-500 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <SafeIcon icon={FiTarget} className="text-2xl text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Step 2: Define Your Launch Goals
          </h2>
          <p className="text-gray-600">
            Clear goals guide your strategy. What does success look like for your book launch?
          </p>
        </div>

        <div className="space-y-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <label className="block text-lg font-semibold text-gray-700 mb-4">
              What's your primary launch goal?
            </label>
            <div className="grid md:grid-cols-2 gap-3">
              {goalOptions.map((goal) => (
                <motion.button
                  key={goal.value}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleInputChange('launchGoal', goal.label)}
                  className={`p-4 rounded-xl border-2 text-left transition-all flex items-center ${
                    formData.launchGoal === goal.label
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <SafeIcon icon={goal.icon} className="text-xl mr-3 flex-shrink-0" />
                  <span className="font-medium">{goal.label}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <label className="block text-lg font-semibold text-gray-700 mb-4">
              What specific metric defines success?
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {metricOptions.map((metric) => (
                <motion.button
                  key={metric}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleInputChange('launchMetrics', metric)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    formData.launchMetrics === metric
                      ? 'border-pink-500 bg-pink-50 text-pink-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <span className="font-medium">{metric}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <label className="block text-lg font-semibold text-gray-700 mb-4">
              What's your timeline for this goal?
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {timelineOptions.map((timeline) => (
                <motion.button
                  key={timeline}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleInputChange('timeline', timeline)}
                  className={`p-3 rounded-xl border-2 text-center transition-all font-medium ${
                    formData.timeline === timeline
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  {timeline}
                </motion.button>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="bg-purple-50 p-6 rounded-xl"
          >
            <h3 className="font-semibold text-purple-800 mb-2">
              🎯 Why Goals Matter
            </h3>
            <p className="text-purple-700">
              Clear goals help you choose the right marketing strategies and measure what's working. 
              Whether you're aiming for sales, impact, or audience growth - knowing your target 
              makes every decision easier.
            </p>
          </motion.div>
        </div>

        <div className="flex justify-between mt-8">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onPrev}
            className="bg-gray-500 text-white px-6 py-3 rounded-xl font-semibold flex items-center hover:bg-gray-600 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back
          </motion.button>

          <motion.button
            whileHover={{ scale: canProceed ? 1.05 : 1 }}
            whileTap={{ scale: canProceed ? 0.95 : 1 }}
            onClick={onNext}
            disabled={!canProceed}
            className={`px-6 py-3 rounded-xl font-semibold flex items-center transition-all ${
              canProceed
                ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Continue
            <SafeIcon icon={FiArrowRight} className="ml-2" />
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default Step2;